<template>
  <VueFinalModal
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-6xl h-[85vh] bg-[#fafbfc] rounded-[40px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
  >
    <div class="flex w-full h-full min-h-0">
      <CreatePodcastSidebar
        :background-image-url="sidebarBackgroundUrl"
        :cover-image-url="sidebarCoverUrl"
        :title="form.title"
        :subtitle="sidebarSubtitle"
        :audio-url="sidebarAudioUrl"
      />

      <div class="flex-1 flex flex-col bg-[#fafbfc] relative overflow-hidden min-w-0">
        <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-30">
          <div>
            <div class="flex items-center gap-2 text-[#1c4848]/40 mb-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0"><path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
              <span class="text-[10px] font-bold uppercase tracking-wider">/ Podcasts / New</span>
            </div>
            <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">Create New Podcast</h2>
          </div>
          <div class="flex gap-3">
            <button
              type="button"
              class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5 transition-colors"
              @click="close"
            >
              Cancel
            </button>
            <button
              type="button"
              :disabled="!canSave"
              class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#1c4848]"
              @click="save"
            >
              <i class="pr pr-upload-track block"></i>
              Publish Podcast
            </button>
          </div>
        </header>

        <div class="flex-1 overflow-y-auto custom-scrollbar p-8">
          <form @submit.prevent="save" class="space-y-8 max-w-3xl mx-auto">
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-document-text text-lg block"></i>
                Episode Details
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div class="md:col-span-2">
                  <TextInput
                    v-model="form.title"
                    label="Episode Title"
                    placeholder="Enter episode title..."
                    input-id="podcast-title"
                    right-icon-class="pr pr-edit"
                  />
                </div>
                <div class="md:col-span-2">
                  <SelectInput
                    v-model="form.authorId"
                    label="Host / Author"
                    select-id="podcast-host"
                    :options="hostOptions"
                    placeholder="Select Host"
                  />
                </div>
                <div class="md:col-span-2">
                  <TextareaInput
                    v-model="form.description"
                    label="Show Notes / Description"
                    placeholder="Write something about this episode..."
                    :rows="4"
                    textarea-id="podcast-description"
                  />
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-gallery text-lg block"></i>
                Media Assets
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
                <div class="md:col-span-1 flex flex-col min-h-0">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1 shrink-0">Cover Art</label>
                  <div class="w-full aspect-square shrink-0">
                    <ImageDropzone
                      v-model="form.coverFile"
                      input-id="podcast-cover"
                      size="small"
                      class="h-full w-full"
                    />
                  </div>
                </div>
                <div class="md:col-span-2 flex flex-col min-h-0">
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1 shrink-0">Audio File</label>
                  <div class="flex-1 min-h-0 flex flex-col">
                    <AudioDropzone
                      v-model="form.audioFile"
                      input-id="podcast-audio"
                      class="flex-1 min-h-0"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section class="pb-6">
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-settings text-lg block"></i>
                Episode Configuration
              </h4>
              <div class="bg-white rounded-3xl p-6 border border-[#1c4848]/5 shadow-sm space-y-6">
                <div class="grid grid-cols-2 md:grid-cols-3 gap-4 pb-6 border-b border-[#1c4848]/5">
                  <div>
                    <label class="block text-[9px] font-bold text-[#1c4848]/40 uppercase tracking-wider mb-1.5">Season No.</label>
                    <input
                      v-model.number="form.seasonNo"
                      type="number"
                      placeholder="1"
                      class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10"
                    />
                  </div>
                  <div>
                    <label class="block text-[9px] font-bold text-[#1c4848]/40 uppercase tracking-wider mb-1.5">Episode No.</label>
                    <input
                      v-model.number="form.episodeNo"
                      type="number"
                      placeholder="1"
                      class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10"
                    />
                  </div>
                  <div>
                    <label class="block text-[9px] font-bold text-[#1c4848]/40 uppercase tracking-wider mb-1.5">Visibility</label>
                    <select
                      v-model="form.visibility"
                      class="w-full bg-[#e1f5e8] text-emerald-700 border-none rounded-xl px-3 py-2 text-xs font-bold focus:ring-2 focus:ring-emerald-500/20 cursor-pointer"
                    >
                      <option value="PUBLIC">Public</option>
                      <option value="PRIVATE">Private</option>
                      <option value="UNLISTED">Unlisted</option>
                    </select>
                  </div>
                </div>
                <div class="flex flex-col md:flex-row gap-6 md:gap-10">
                  <ToggleInput
                    v-model="form.explicit"
                    title="Explicit Content"
                    description="Contains mature language"
                  />
                  <ToggleInput
                    v-model="form.featured"
                    title="Featured Episode"
                    description="Pin to top of feed"
                  />
                  <ToggleInput
                    v-model="form.commentsEnabled"
                    title="Allow Comments"
                    description="Community interaction"
                  />
                </div>
              </div>
            </section>
          </form>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import CreatePodcastSidebar from '@/components/podcast-modal/CreatePodcastSidebar.vue';
import TextInput from '@/components/forms/text/TextInput.vue';
import SelectInput from '@/components/forms/select/SelectInput.vue';
import TextareaInput from '@/components/forms/textarea/TextareaInput.vue';
import ToggleInput from '@/components/forms/toggle/ToggleInput.vue';
import ImageDropzone from '@/components/forms/dropzone/ImageDropzone.vue';
import AudioDropzone from '@/components/forms/dropzone/AudioDropzone.vue';
import { usePodcastStore } from '@/store/podcasts/index.js';
import { useAuthorsStore } from '@/store/authors/index.js';
import { usePagesStore } from '@/store/pages/index.js';

const emit = defineEmits(['confirm', 'created', 'close']);

const podcastStore = usePodcastStore();
const authorsStore = useAuthorsStore();
const pagesStore = usePagesStore();

const defaultBgUrl = 'https://fra1.digitaloceanspaces.com/aplusspace/69/12c93ede1d5bbc246b9022e680238d18c1d0c2ec.png';

const form = ref({
  title: '',
  authorId: '',
  description: '',
  coverFile: null,
  audioFile: null,
  seasonNo: 1,
  episodeNo: 1,
  visibility: 'PUBLIC',
  explicit: false,
  featured: false,
  commentsEnabled: true,
});

onMounted(() => {
  authorsStore.fetchAuthors();
});

const sidebarBackgroundUrl = computed(() => defaultBgUrl);

const coverObjectUrl = ref('');
const audioObjectUrl = ref('');

watch(
  () => form.value.coverFile,
  (f) => {
    if (coverObjectUrl.value) URL.revokeObjectURL(coverObjectUrl.value);
    coverObjectUrl.value = f instanceof File ? URL.createObjectURL(f) : '';
  },
  { immediate: true }
);
watch(
  () => form.value.audioFile,
  (f) => {
    if (audioObjectUrl.value) URL.revokeObjectURL(audioObjectUrl.value);
    audioObjectUrl.value = f instanceof File ? URL.createObjectURL(f) : '';
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (coverObjectUrl.value) URL.revokeObjectURL(coverObjectUrl.value);
  if (audioObjectUrl.value) URL.revokeObjectURL(audioObjectUrl.value);
});

const sidebarCoverUrl = computed(() => coverObjectUrl.value);
const sidebarAudioUrl = computed(() => audioObjectUrl.value);

const hostOptions = computed(() => {
  const list = authorsStore.authors ?? [];
  return [
    { value: '', label: 'Select Host' },
    ...list.map((a) => ({ value: String(a.id), label: a.nickname ?? a.name ?? '—' })),
  ];
});

const sidebarSubtitle = computed(() => {
  const id = form.value.authorId;
  if (!id) return 'Host Name • Season ' + (form.value.seasonNo || 1);
  const author = authorsStore.authors?.find((a) => String(a.id) === id);
  const name = author?.nickname ?? author?.name ?? 'Host';
  return `${name} • Season ${form.value.seasonNo || 1}`;
});

const canSave = computed(
  () => form.value.title?.trim() && form.value.coverFile instanceof File && form.value.audioFile instanceof File
);

function close() {
  emit('confirm');
  emit('close');
}

async function save() {
  if (!canSave.value) return;
  try {
    const payload = {
      title: form.value.title.trim(),
      author_id: form.value.authorId || null,
      description: form.value.description?.trim() || null,
      season_no: form.value.seasonNo,
      episode_no: form.value.episodeNo,
      visibility: form.value.visibility,
      is_explicit: form.value.explicit,
      is_featured: form.value.featured,
      comments_enabled: form.value.commentsEnabled,
    };
    const newPodcast = await podcastStore.addPodcast(payload);
    await podcastStore.uploadPodcastImage(newPodcast.id, form.value.coverFile);
    await podcastStore.uploadPodcastMp3(newPodcast.id, form.value.audioFile);
    if (pagesStore.selectedPage?.id) {
      await pagesStore.attachPodcastToPage(pagesStore.selectedPage.id, newPodcast.id);
    }
    emit('created');
    close();
  } catch (err) {
    console.error(err);
    alert('Failed to save podcast. Please try again.');
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(28, 72, 72, 0.2);
  border-radius: 3px;
}
</style>
